import { useState, useRef, useEffect, KeyboardEvent, MouseEvent } from 'react';
import styles from './MultiSelect.module.scss';
import { MultiSelectOption } from './MultiSelectOption/MultiSelectOption';
import classNames from 'classnames';
import ArrowDown from 'ui/icons/icon-chevron-down.svg';

export interface SubOption {
	label: string;
	value: string;
}

export interface Option {
	label: string;
	value: string;
	subOptions?: SubOption[];
}

export interface MultiSelectProps {
	className?: string;
	options: Option[];
	texts: {
		label: string;
		placeholder: string;
		selected: string;
	};
	value: string[]; // selected suboption values
	onChange: (selectedValues: string[]) => void;
	id: string;
}

export const UI_MultiSelect = ({ className, options, texts, value, onChange, id }: MultiSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [expandedParents, setExpandedParents] = useState<string[]>([]);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const listRef = useRef<HTMLUListElement>(null);

	const toggleDropdown = () => setIsOpen((prev) => !prev);
	const toggleParentExpansion = (optionValue: string) => {
		setExpandedParents(
			(prev) =>
				prev.includes(optionValue)
					? prev.filter((v) => v !== optionValue) // remove if already expanded
					: [...prev, optionValue], // add if not expanded
		);
	};
	const ensureParentExpanded = (optionValue: string) => {
		setExpandedParents((prev) => {
			if (prev.includes(optionValue)) return prev; // already expanded
			return [...prev, optionValue]; // add if not expanded
		});
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent | globalThis.MouseEvent) => {
			if (listRef.current && !listRef.current.contains(e.target as Node) && !buttonRef.current?.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleOptionClick = (option: Option) => {
		const allSubValues = option.subOptions?.map((s) => s.value) ?? [];
		const isParentSelected = allSubValues.every((v) => value.includes(v));

		let newSelected: string[];

		if (allSubValues.length === 0) {
			// Parent has no suboptions, toggle its value directly
			const isSelected = value.includes(option.value);
			newSelected = isSelected ? value.filter((v) => v !== option.value) : [...value, option.value];
			return onChange(newSelected);
		}

		// Deselect all suboptions
		if (isParentSelected) {
			newSelected = value.filter((v) => !allSubValues.includes(v));
			setExpandedParents(
				(prev) => prev.filter((v) => v !== option.value), // remove if already expanded
			);
		}

		// Select all suboptions
		if (!isParentSelected) {
			newSelected = Array.from(new Set([...value, ...allSubValues]));
			ensureParentExpanded(option.value);
		}

		onChange(newSelected);
	};

	const handleSubOptionClick = (sub: SubOption) => {
		const isSelected = value.includes(sub.value);
		let newSelected: string[];

		// Deselect suboption
		if (isSelected) newSelected = value.filter((v) => v !== sub.value);

		// Select suboption
		if (!isSelected) newSelected = [...value, sub.value];

		onChange(newSelected);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLElement>, handler: () => void) => {
		if (e.key === ' ' || e.key === 'Enter') {
			e.preventDefault();
			handler();
		} else if (e.key === 'Escape') {
			setIsOpen(false);
			buttonRef.current?.focus();
		}
	};

	const labelId = `${id}-multiselect-label`;
	const listId = `${id}-multiselect-list`;
	return (
		<div className={classNames(styles.MultiSelect, className)}>
			<label id={labelId} className={styles.MultiSelect_label}>
				{texts.label}
			</label>

			<button
				type="button"
				ref={buttonRef}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={listId}
				aria-labelledby={labelId}
				onClick={toggleDropdown}
				className={styles.MultiSelect_button}
			>
				{value.length > 0 ? `${value.length} ${texts.selected}` : texts.placeholder}
				<span className={styles.MultiSelect_icon}>
					<ArrowDown />
				</span>
			</button>

			{isOpen && (
				<ul ref={listRef} role="listbox" aria-multiselectable="true" tabIndex={-1} className={styles.MultiSelect_list} id={listId}>
					{options.map((option) => {
						const totalValues = option.subOptions?.map((s) => s.value) ?? [];
						const selectedValues = totalValues.filter((v) => value.includes(v));
						const isParentSelected =
							totalValues.length === 0
								? value.includes(option.value)
								: selectedValues.length === totalValues.length && totalValues.length > 0;
						const isIndeterminate = totalValues.length > 0 && selectedValues.length > 0 && selectedValues.length < totalValues.length;

						return (
							<MultiSelectOption
								key={option.value}
								option={option}
								isParentSelected={isParentSelected}
								isIndeterminate={isIndeterminate}
								expandedParents={expandedParents}
								toggleParentExpansion={toggleParentExpansion}
								handleOptionClick={handleOptionClick}
								handleSubOptionClick={handleSubOptionClick}
								handleKeyDown={handleKeyDown}
								value={value}
							/>
						);
					})}
				</ul>
			)}
		</div>
	);
};
